// theme.js
import { createTheme } from '@mui/material/styles';

// Extend the default theme interface to include our custom background and text colors
declare module '@mui/material/styles' {
    interface TypeBackground {
        dark: string;
        accent: string;
    }
    interface TypeText {
        tertiary: string;
    }
    interface Palette {
        accent: {
            main: string;
            light: string;
            dark: string;
        };
    }
    interface PaletteOptions {
        accent?: {
            main: string;
            light: string;
            dark: string;
        };
    }
}

const theme = createTheme({
    typography: {
        fontFamily: [
            'System Font',
            '-apple-system',
            'BlinkMacSystemFont',
            'Segoe UI',
            'Roboto',
            'Oxygen-Sans',
            'Ubuntu',
            'Cantarell',
            'Helvetica Neue',
            'sans-serif',
        ].join(','),
        h1: {
            fontFamily: '"Playfair Display", Inter, sans-serif',
            fontWeight: 500,
            fontSize: '1.5rem',
            lineHeight: '2rem',
            letterSpacing: '-0.01em',
            color: '#2A2A2A', // heading-dark from tailwind config
        },
        h2: {
            fontFamily: '"Playfair Display", Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1.3rem',
            lineHeight: '1.5rem',
            letterSpacing: '-0.01em',
            color: '#2A2A2A',
        },

        // For serif text elements
        body1: {
            fontFamily: 'Playfair Display, serif',
        },
    },
    palette: {
        mode: 'light',
        primary: {
            main: '#2B2E4A',      // celestial dark blue
            light: '#3D4066',     // lighter version for hover states
            dark: '#1F2137',      // darker version for active states

            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#C9ADA7',      // moon color
            light: '#D6C0BA',     // lighter version
            dark: '#B89690',      // darker version
            contrastText: '#2B2E4A',
        },
        accent: {
            main: '#C4A15A',      // golden/bronze accent
            light: '#D4B87A',     // lighter version
            dark: '#A8854A',      // darker version
        },
        success: {
            main: '#4CAF50',      // green
            light: '#81C784',     // lighter green
            dark: '#388E3C',      // darker green
            contrastText: '#FFFFFF',
        },
        error: {
            main: '#9E4F50',      // muted burgundy red
            light: '#B66E6F',     // lighter version
            dark: '#833A3B',      // darker version
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#F5F3F0',
            paper: '#F8F1EE',
            dark: '#2B2E4A',
            accent: 'rgba(196, 161, 90, 0.08)',
        },
        text: {
            primary: '#2C2C2C',
            secondary: '#6B6B6B',
            tertiary: 'rgba(213, 183, 107, 0.3)',      // gold color
        },
        action: {
            active: '#2B2E4A',
            hover: 'rgba(43, 46, 74, 0.08)',
            selected: 'rgba(43, 46, 74, 0.12)',
            disabled: 'rgba(43, 46, 74, 0.38)',
            disabledBackground: 'rgba(43, 46, 74, 0.12)',
        },
        divider: 'rgba(0, 0, 0, 0.12)',
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    padding: '10px 16px',
                    textTransform: 'none',
                    '&.Mui-disabled': {
                        color: 'rgba(43, 46, 74, 0.38)',
                        backgroundColor: 'rgba(43, 46, 74, 0.12)',
                    },
                },
                contained: {
                    backgroundColor: '#2B2E4A',
                    color: '#fff',
                    '&:hover': {
                        backgroundColor: '#3D4066',
                    },
                    '&:active': {
                        backgroundColor: '#1F2137',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    fontWeight: 500,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(43, 46, 74, 0.08)',
                        '&:hover': {
                            backgroundColor: 'rgba(43, 46, 74, 0.12)',
                        },
                    },
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#F8F1EE',
                },
            },
        },
    },
});

export default theme;
